import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface InfoModalProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  // When true the OK button spans the modal width (matches the find-id/pending prototype).
  confirmFullWidth?: boolean;
  /**
   * SHCT-861 — whether the backdrop and Escape also close the modal. Default `true`,
   * which is the prototype's behaviour and what every existing caller relies on.
   *
   * Pass `false` only when the acknowledgement itself carries a consequence the user
   * must not be able to trigger by a stray click — the duplicate-login notice runs the
   * session teardown and the jump to /login on close (PRD 00 §8.3 `얼럿 후 초기화`), so
   * QC asked that only [확인] dismiss it. With `false` the two casual exits do nothing
   * at all; they do not close the modal and they do not run `onClose`.
   */
  dismissible?: boolean;
  onClose: () => void;
}

// Single-button informational modal. Matches the prototype's pending/find-id dialogs:
// a title, a body region, one OK button, plus backdrop click + Escape to close.
// Used wherever the user only acknowledges — for confirm/cancel see ConfirmModal.
export function InfoModal({
  open,
  title,
  body,
  confirmLabel = '확인',
  confirmFullWidth = false,
  dismissible = true,
  onClose,
}: InfoModalProps) {
  useEffect(() => {
    // SHCT-861 — with `dismissible={false}` the listener is never attached, so Escape
    // falls through to whatever is underneath rather than being swallowed silently.
    if (!open || !dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismissible, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      onClick={dismissible ? onClose : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
        </div>
        <div className="modal__body">
          <div className="modal__message" style={{ whiteSpace: 'pre-line' }}>
            {body}
          </div>
        </div>
        <div
          className="modal__footer"
          style={{ justifyContent: confirmFullWidth ? 'stretch' : 'center' }}
        >
          <button
            type="button"
            className="btn btn-default btn-primary"
            style={confirmFullWidth ? { width: '100%' } : { minWidth: 120 }}
            onClick={onClose}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
